import { useEffect, useRef, useState } from 'react';
import { transformations } from '@/mocks/homepage';

export default function TransformationSection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="transformation" ref={ref} className="bg-cream-50 py-24 md:py-36">
      <div className="w-full px-6 md:px-10 lg:px-16">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div
            className={`text-center mb-16 md:mb-24 transition-all duration-1000 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <p className="text-sage-500 text-xs tracking-[0.3em] uppercase mb-4 font-medium">
              The Transformation
            </p>
            <h2 className="font-serif text-3xl md:text-5xl text-charcoal-300 font-medium leading-tight max-w-3xl mx-auto">
              What changes when you start
              <br />
              <span className="italic font-light">practicing stewardship?</span>
            </h2>
          </div>

          {/* From / To Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
            {/* FROM Column */}
            <div
              className={`transition-all duration-1000 delay-200 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
            >
              <p className="text-sand-500 text-xs tracking-[0.2em] uppercase mb-6 font-medium">
                FROM
              </p>
              <div className="space-y-4">
                {transformations.map((t, i) => (
                  <div
                    key={`from-${i}`}
                    className="bg-cream-200/60 rounded-xl px-6 py-5 flex items-center gap-4"
                  >
                    <div className="w-2 h-2 rounded-full bg-sand-400 flex-shrink-0" />
                    <span className="text-charcoal-100 text-lg md:text-xl font-medium">
                      {t.from}
                    </span>
                  </div>
                ))}
                <div className="bg-cream-200/60 rounded-xl px-6 py-5 flex items-center gap-4">
                  <div className="w-2 h-2 rounded-full bg-sand-400 flex-shrink-0" />
                  <span className="text-charcoal-100 text-lg md:text-xl font-medium">
                    Trapped
                  </span>
                </div>
              </div>
            </div>

            {/* Arrow - Desktop */}
            <div className="hidden md:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 items-center justify-center z-10">
              <div className="w-12 h-12 rounded-full bg-sage-400 flex items-center justify-center">
                <i className="ri-arrow-right-line text-white text-xl" />
              </div>
            </div>

            {/* TO Column */}
            <div
              className={`transition-all duration-1000 delay-400 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
            >
              <p className="text-sage-500 text-xs tracking-[0.2em] uppercase mb-6 font-medium">
                TO
              </p>
              <div className="space-y-4">
                {transformations.map((t, i) => (
                  <div
                    key={`to-${i}`}
                    className="bg-sage-100/60 rounded-xl px-6 py-5 flex items-center gap-4"
                  >
                    <div className="w-2 h-2 rounded-full bg-sage-400 flex-shrink-0" />
                    <span className="text-charcoal-300 text-lg md:text-xl font-medium">
                      {t.to}
                    </span>
                  </div>
                ))}
                <div className="bg-sage-100/60 rounded-xl px-6 py-5 flex items-center gap-4">
                  <div className="w-2 h-2 rounded-full bg-sage-400 flex-shrink-0" />
                  <span className="text-charcoal-300 text-lg md:text-xl font-medium">
                    Hopeful
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom reflection */}
          <div
            className={`mt-16 md:mt-24 text-center transition-all duration-1000 delay-600 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            <p className="font-serif text-xl md:text-2xl text-charcoal-300 italic leading-relaxed max-w-2xl mx-auto">
              The practice does not promise wealth.
              <br />
              It promises <span className="text-sage-500 font-medium">peace.</span>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}